/*
Copyright 2023, 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { useCallback, useEffect, useMemo } from "react";
import { logger } from "matrix-js-sdk/lib/logger";

import {
  setLocalStorageItemReactive,
  useLocalStorage,
} from "../useLocalStorage";
import { getUrlParams } from "../UrlParams";
import { E2eeType } from "./e2eeType";
import { useClient } from "../ClientContext";

/**
 * This setter will update the state for all `useRoomSharedKey` hooks
 * if the password is different from the one in local storage or if its not yet in the local storage.
 */
export function saveKeyForRoom(roomId: string, password: string): void {
  if (
    localStorage.getItem(getRoomSharedKeyLocalStorageKey(roomId)) !== password
  )
    setLocalStorageItemReactive(
      getRoomSharedKeyLocalStorageKey(roomId),
      password,
    );

  if (localStorage.getItem(getRoomSharedKeyTrustedKey(roomId)) !== "true")
    setLocalStorageItemReactive(getRoomSharedKeyTrustedKey(roomId), "true");
}

const getRoomSharedKeyLocalStorageKey = (roomId: string): string =>
  `room-shared-key-${roomId}`;

const getRoomSharedKeyTrustedKey = (roomId: string): string =>
  `room-shared-key-${roomId}-trusted`;

const getTrustedStoredKeyForRoom = (roomId: string): string | null => {
  if (localStorage.getItem(getRoomSharedKeyTrustedKey(roomId)) !== "true")
    return null;
  return localStorage.getItem(getRoomSharedKeyLocalStorageKey(roomId));
};

/**
 * An up-to-date shared key for the room. Either from local storage or the value from `setInitialValue`.
 * @param roomId The room ID we want the shared key for.
 * @param setInitialValue The value we get from the URL. The hook will overwrite the local storage value with this.
 * @returns [roomSharedKey, setRoomSharedKey] like a react useState hook.
 */
export const useRoomSharedKey = (
  roomId: string,
  setInitialValue?: string,
): [string | null, setKey: (key: string) => void] => {
  const [roomSharedKey, setRoomSharedKey] = useLocalStorage(
    getRoomSharedKeyLocalStorageKey(roomId),
  );
  const [trustedStoredKey, setTrustedStoredKey] = useLocalStorage(
    getRoomSharedKeyTrustedKey(roomId),
  );

  useEffect(() => {
    // If setInitialValue is available, update the local storage (usually the password from the url).
    // This will update roomSharedKey but wont update the returned value since
    // that already defaults to setInitialValue.
    if (setInitialValue) {
      setRoomSharedKey(setInitialValue);
      setTrustedStoredKey("true");
    }
  }, [setInitialValue, setRoomSharedKey, setTrustedStoredKey]);

  const setPersistentRoomSharedKey = useCallback(
    (key: string) => {
      setRoomSharedKey(key);
      setTrustedStoredKey("true");
    },
    [setRoomSharedKey, setTrustedStoredKey],
  );

  // make sure we never return the initial null value from `useLocalStorage`
  return [
    setInitialValue ?? (trustedStoredKey === "true" ? roomSharedKey : null),
    setPersistentRoomSharedKey,
  ];
};

export function getKeyForRoom(roomId: string): string | null {
  const { roomId: urlRoomId, password } = getUrlParams();
  if (roomId !== urlRoomId)
    logger.warn(
      "requested key for a roomId which is not the current call room id (from the URL)",
      roomId,
      urlRoomId,
    );
  return password ?? getTrustedStoredKeyForRoom(roomId);
}

export type Unencrypted = { kind: E2eeType.NONE };
export type SharedSecret = { kind: E2eeType.SHARED_KEY; secret: string };
export type PerParticipantE2EE = { kind: E2eeType.PER_PARTICIPANT };
export type EncryptionSystem = Unencrypted | SharedSecret | PerParticipantE2EE;

type MediaEncryptionDecision = {
  isMatrixRoomEncrypted: boolean;
  sharedSecret?: string | null;
  perParticipantE2EE?: boolean;
  mediaE2EEDisabled?: boolean;
};

export function mediaEncryptionSystemForRoom({
  isMatrixRoomEncrypted,
  sharedSecret,
  perParticipantE2EE = false,
  mediaE2EEDisabled = false,
}: MediaEncryptionDecision): EncryptionSystem {
  const nonEmptySharedSecret = sharedSecret?.trim() || null;

  if (mediaE2EEDisabled) return { kind: E2eeType.NONE };

  // An embedding client can explicitly request automatic participant keys.
  if (perParticipantE2EE) return { kind: E2eeType.PER_PARTICIPANT };
  if (nonEmptySharedSecret)
    return {
      kind: E2eeType.SHARED_KEY,
      secret: nonEmptySharedSecret,
    };

  return {
    kind: isMatrixRoomEncrypted ? E2eeType.PER_PARTICIPANT : E2eeType.NONE,
  };
}

/** Ordinary calls ignore cached meeting keys; explicit links select shared-key mode. */
export function callSharedSecret(
  explicitPassword: string | null | undefined,
  storedPassword: string | null,
  isMeetingRoom: boolean,
  perParticipantE2EE: boolean,
): string | null {
  if (perParticipantE2EE) return null;
  return (
    explicitPassword?.trim() ||
    (isMeetingRoom ? storedPassword?.trim() || null : null)
  );
}

/** Matches the dedicated meeting rooms created by the mobile app. */
export function isPasswordProtectedMeetingRoom(
  room:
    | {
        name: string;
        currentState: {
          getStateEvents(
            type: string,
            stateKey: string,
          ): { getContent(): { topic?: string } } | null;
        };
      }
    | null
    | undefined,
): boolean {
  return (
    room?.name === "ADCMC Ready Call" &&
    room.currentState.getStateEvents("m.room.topic", "")?.getContent().topic ===
      "Call-only meeting"
  );
}

export function useRoomEncryptionSystem(
  roomId: string,
  roomSharedKeyOverride?: string | null,
): EncryptionSystem {
  const { client } = useClient();

  const [storedPassword] = useRoomSharedKey(
    roomId,
    getKeyForRoom(roomId) ?? undefined,
  );

  const room = client?.getRoom(roomId);
  const params = getUrlParams();
  // Local meeting history must not silently change ordinary room-call encryption.
  const explicitPassword = params.roomId === roomId ? params.password : null;
  const roomSharedKey =
    roomSharedKeyOverride !== undefined
      ? roomSharedKeyOverride
      : callSharedSecret(
          explicitPassword,
          storedPassword,
          isPasswordProtectedMeetingRoom(room),
          params.perParticipantE2EE,
        );
  const e2eeSystem = <EncryptionSystem>useMemo(() => {
    return mediaEncryptionSystemForRoom({
      isMatrixRoomEncrypted: room?.hasEncryptionStateEvent() ?? false,
      sharedSecret: roomSharedKey,
      perParticipantE2EE: params.perParticipantE2EE,
      mediaE2EEDisabled:
        !!params.widgetId && !params.perParticipantE2EE && !roomSharedKey,
    });
  }, [room, roomSharedKey, params.perParticipantE2EE, params.widgetId]);
  return e2eeSystem;
}
